import IcoMoon from '@/components/IcoMoon'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useAi } from '@/context/ai'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import { AIElementNames, eventsTracker } from '@/lib/eventsTracker'
import RelatedArticles from '@/widgets/v2/AI/Sidebar/Accordion/RelatedArticles'
import RelatedClasses from '@/widgets/v2/AI/Sidebar/Accordion/RelatedClasses'
import RelatedLiveClasses from '@/widgets/v2/AI/Sidebar/Accordion/RelatedLiveClasses'
import RelatedReminders from '@/widgets/v2/AI/Sidebar/Accordion/RelatedReminders'
import { useEffect, useRef, useState } from 'react'

interface Props {
  toggleResources: () => void
  isResourceOpen: boolean
}

const AccordionSidebar = (props: Props): JSX.Element => {
  const {
    state: { query: searchQuery },
  } = useAi()
  const {
    state: { tenantId },
  } = useAuth()
  const [accordionValue, setAccordionValue] = useState<string>('classes')

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const handleResize = (): void => {
    if (!sidebarRef || !sidebarRef.current) return
    const sidebarRect = sidebarRef.current.getBoundingClientRect()
    setWidth(sidebarRect.width)
    setHeight(sidebarRect.height)
  }
  const showLiveClasses = useFeatureToggle('helen_live_classes')

  useEffect(() => {
    if (!sidebarRef || !sidebarRef.current) return
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [sidebarRef])

  // Reset the accordion on the new messages and set the default tab to Related Classes
  useEffect(() => {
    setAccordionValue('classes')
  }, [searchQuery])

  return (
    <div onLoad={handleResize} ref={sidebarRef} className="z-50 w-full h-full">
      <div
        onClick={props.toggleResources}
        className="fixed right-4 sm:right-[38px] top-3 flex lg:hidden items-center gap-1 px-4 py-2 bg-[#E0E9FF] rounded-[40px] min-w-[102px] min-h-[40px]"
      >
        <IcoMoon icon="close" size={20} />
        <span className="text-sm font-semibold">Close</span>
      </div>
      <div className="mt-4 pt-4 lg:pt-0" style={{ width }}>
        {props.isResourceOpen && (
          <Accordion
            type="single"
            collapsible
            className="w-full tablet:mt-20 desktop:mt-4"
            value={accordionValue}
            onValueChange={(value) => {
              setAccordionValue(value)
              if (value) eventsTracker.handleOnClickWithDistinctionAndTrackEvent(AIElementNames.BUTTON_ACCORDION, value)
            }}
          >
            {tenantId && (
              <AccordionItem value="classes">
                <AccordionTrigger className="text-lg font-bold">Suggested Classes - Watch Now</AccordionTrigger>
                <AccordionContent>
                  <RelatedClasses tenantId={tenantId} />
                </AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="articles">
              <AccordionTrigger className="text-lg font-bold">Suggested Articles</AccordionTrigger>
              <AccordionContent>
                <RelatedArticles height={height} />
              </AccordionContent>
            </AccordionItem>
            {showLiveClasses && tenantId && (
              <AccordionItem value="liveClasses">
                <AccordionTrigger className="text-lg font-bold">
                  Suggested Scheduled Classes - Watch Later
                </AccordionTrigger>
                <AccordionContent>
                  <RelatedLiveClasses tenantId={tenantId} />
                </AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="reminders">
              <AccordionTrigger className="text-lg font-bold">Suggested Reminders</AccordionTrigger>
              <AccordionContent>
                <RelatedReminders height={height} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </div>
  )
}

export default AccordionSidebar
